import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography, TextField, Chip, InputAdornment, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import TuneIcon from '@mui/icons-material/Tune';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ViewListIcon from '@mui/icons-material/ViewList';
import AddIcon from '@mui/icons-material/Add';
import Dashboard from '../../components/containers/Dashboard/Dashboard';
import { CATEGORY_LABELS } from '../../data/boardTemplatesData';

const navy    = '#1e3a5f';
const navyBg  = '#f0f4f8';
const border  = '#e2e8f0';
const slate   = '#64748b';
const cream   = '#fcfbfa';

// Mini thumbnail — lightweight CSS-grid representation
const MiniThumbnail = ({ template }) => (
  <Box sx={{
    width: 72, height: 41, flexShrink: 0,
    position: 'relative', overflow: 'hidden',
    bgcolor: template?.settings?.bgColor || cream,
    borderRadius: '3px',
    border: `1px solid ${border}`,
  }}>
    {template?.widgets?.map((widget, idx) => {
      const { x = 0, y = 0, w = 4, h = 4 } = widget.dashboardLayout || {};
      return (
        <Box key={idx} sx={{
          position: 'absolute',
          left:   `${(x / 24) * 100}%`,
          top:    `${(y / 28) * 100}%`,
          width:  `${(w / 24) * 100}%`,
          height: `${(h / 28) * 100}%`,
          bgcolor: widget.widgetSettings?.bgColor || '#dde8f4',
          border: '0.5px solid rgba(30,58,95,0.12)',
          borderRadius: '1px',
        }} />
      );
    })}
  </Box>
);

// Full-board preview
// Dashboard.jsx has its own internal ResizeObserver and scale computation:
//   scale = min(containerW/1920, containerH/1080) * 0.98
// So we simply give it a sized container and let it handle the rest — no extra CSS transform needed.
const BoardPreview = ({ template, organization, catalog, dispatch }) => {
  if (!template || template.id === 'empty') {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: navyBg }}>
        <Box sx={{ textAlign: 'center', color: slate }}>
          <Typography sx={{ fontSize: '3rem', lineHeight: 1, mb: 1.5 }}>🖼️</Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: navy, mb: 0.5 }}>לוח ריק</Typography>
          <Typography sx={{ fontSize: '0.85rem' }}>התחל מלוח ריק ובנה אותו מאפס</Typography>
        </Box>
      </Box>
    );
  }

  const dashboard = {
    dashboardId: `preview-${template.id}`,
    name: template.name || 'preview',
    current: {
      widgets: (template.widgets || []).map((widget, i) => ({
        ...widget,
        widgetId: widget.widgetId || `prev-${template.id}-${i}`,
        widgetSettings: widget.widgetSettings || {},
      })),
      settings: template.settings || { bgColor: cream, bgImage: '' },
    },
    versions: [],
  };

  return (
    // position: relative + overflow: hidden → Dashboard fills this box and computes its own scale
    <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', direction: 'ltr' }}>
      <Dashboard
        dispatch={dispatch}
        organization={organization || { organizationId: 'preview', address: { city: 'ירושלים', country: 'ישראל' }, location: { lat: 31.7683, lng: 35.2137 } }}
        catalog={catalog}
        dashboard={dashboard}
        isEditMode={false}
        hasUnsavedChanges={false}
        isSaving={false}
      />
    </Box>
  );
};

// Source mode button
const SourceModeButton = ({ active, icon, label, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      flex: 1,
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
      py: 0.75, px: 0.5,
      borderRadius: '8px',
      cursor: 'pointer',
      bgcolor: active ? navy : 'white',
      color: active ? 'white' : slate,
      border: `1.5px solid ${active ? navy : border}`,
      transition: 'all 0.15s',
      '&:hover': { bgcolor: active ? navy : '#f0f4f8' },
    }}
  >
    {React.cloneElement(icon, { sx: { fontSize: '0.85rem' } })}
    <Typography sx={{ fontSize: '0.73rem', fontWeight: 700, lineHeight: 1.2, textAlign: 'center' }}>{label}</Typography>
  </Box>
);

// ── Main component ──────────────────────────────────────────────────────────
/**
 * DashboardTemplateSelector
 * Split-screen template picker with three source modes:
 * 1. Templates — predefined templates
 * 2. Existing boards — copy from an existing dashboard
 * 3. Empty — start from scratch
 */
const DashboardTemplateSelector = ({
  templates = [],
  existingDashboards = [],
  selectedTemplateId,
  onSelectTemplate,
  organization = null,
  catalog = null,
  dispatch = null,
  loading = false,
}) => {
  const [search, setSearch]         = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [sourceMode, setSourceMode] = useState('templates'); // 'templates' | 'existing' | 'empty'

  // Filter out the 'empty' pseudo-template — handled by sourceMode now
  const actualTemplates = useMemo(() =>
    templates.filter(t => t.id !== 'empty'),
  [templates]);

  // Normalize existing dashboards for display/preview
  const existingItems = useMemo(() =>
    existingDashboards
      .filter(d => d.dashboardId)
      .map(d => ({
        id: `copy:${d.dashboardId}`,
        name: d.name || 'לוח ללא שם',
        widgets: d.current?.widgets || [],
        settings: d.current?.settings || {},
        widgetCount: (d.current?.widgets || []).length,
      })),
  [existingDashboards]);

  // Handle source mode changes
  const handleSourceModeChange = (mode) => {
    if (mode === sourceMode) return;
    setSourceMode(mode);
    setSearch('');
    setActiveCategory('all');
    if (mode === 'empty') {
      onSelectTemplate('empty');
    } else {
      onSelectTemplate(null);
    }
  };

  // Auto-select empty when component mounts with empty mode
  useEffect(() => {
    if (sourceMode === 'empty' && selectedTemplateId !== 'empty') {
      onSelectTemplate('empty');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceMode]);

  // Derive category list from actual templates
  const categories = useMemo(() => {
    const seen = new Set();
    actualTemplates.forEach(t => { if (t.category) seen.add(t.category); });
    return Array.from(seen);
  }, [actualTemplates]);

  // Filtered template list (search + category) — only for templates mode
  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return actualTemplates.filter(t => {
      const matchCat = activeCategory === 'all' || t.category === activeCategory;
      const matchQ   = !q || (t.name || '').toLowerCase().includes(q) || (t.concept || '').toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [actualTemplates, activeCategory, search]);

  // Filtered existing dashboards list — only for existing mode
  const filteredExisting = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return existingItems;
    return existingItems.filter(d => d.name.toLowerCase().includes(q));
  }, [existingItems, search]);

  // Selected item object — works across all modes
  const selected = useMemo(() => {
    if (!selectedTemplateId) return null;
    if (selectedTemplateId === 'empty') {
      return { id: 'empty', name: 'לוח ריק', widgets: [], settings: { bgColor: cream, bgImage: '' } };
    }
    if (selectedTemplateId.startsWith('copy:')) {
      return existingItems.find(d => d.id === selectedTemplateId) || null;
    }
    return actualTemplates.find(t => t.id === selectedTemplateId) || null;
  }, [selectedTemplateId, actualTemplates, existingItems]);

  // Resolve widget name from catalog
  const getWidgetName = (componentKey) => {
    if (!catalog || !componentKey) return componentKey || '';
    const found = catalog.find(c => c.componentKey === componentKey);
    return found?.name || componentKey;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 500 }}>
        <CircularProgress size={48} sx={{ color: navy }} />
      </Box>
    );
  }

  return (
    <Box dir="rtl" sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>

      {/* ── hint strip ────────────────────────────────────────────────── */}
      <Box sx={{ px: 3, py: 1.5, bgcolor: '#fffbeb', borderBottom: `1px solid #fde68a`, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
          {/* Editable notice — applies to all modes */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.75,
            bgcolor: '#fff7ed', border: '1.5px solid #fb923c',
            borderRadius: '8px', px: 1.5, py: 0.6,
          }}>
            <EditIcon sx={{ fontSize: '0.95rem', color: '#c2410c' }} />
            <Typography sx={{ fontSize: '0.8rem', color: '#7c2d12', fontWeight: 700 }}>
              ניתן לערוך, להוסיף ולהסיר רכיבים לאחר הוספת הלוח
            </Typography>
          </Box>
          {/* Default data notice — templates mode */}
          {sourceMode === 'templates' && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              bgcolor: '#fefce8', border: '1.5px solid #facc15',
              borderRadius: '8px', px: 1.5, py: 0.6,
            }}>
              <TuneIcon sx={{ fontSize: '0.95rem', color: '#a16207' }} />
              <Typography sx={{ fontSize: '0.8rem', color: '#713f12', fontWeight: 700 }}>
                הרכיבים בתבניות המוכנות מגיעים עם נתוני דמה — מומלץ לעבור רכיב רכיב ולעדכן את ההגדרות
              </Typography>
            </Box>
          )}
          {/* Copy notice — existing mode */}
          {sourceMode === 'existing' && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              bgcolor: '#f0f9ff', border: '1.5px solid #7dd3fc',
              borderRadius: '8px', px: 1.5, py: 0.6,
            }}>
              <ContentCopyIcon sx={{ fontSize: '0.95rem', color: '#0369a1' }} />
              <Typography sx={{ fontSize: '0.8rem', color: '#0c4a6e', fontWeight: 700 }}>
                ייווצר לוח חדש עם אותם רכיבים והגדרות — הלוח המקורי לא יושפע
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Split content ─────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>

        {/* ── LEFT PANEL: list ──────────────────────────────────────── */}
        <Box sx={{
          width: 320, flexShrink: 0,
          borderLeft: `1px solid ${border}`,
          display: 'flex', flexDirection: 'column',
          bgcolor: '#fafbfc',
        }}>
          {/* Source mode toggle */}
          <Box sx={{ p: 1.5, display: 'flex', gap: 0.5, borderBottom: `1px solid ${border}`, flexShrink: 0 }}>
            <SourceModeButton
              active={sourceMode === 'templates'}
              icon={<ViewListIcon />}
              label="תבנית מוכנה"
              onClick={() => handleSourceModeChange('templates')}
            />
            <SourceModeButton
              active={sourceMode === 'existing'}
              icon={<ContentCopyIcon />}
              label="העתקה מלוח קיים"
              onClick={() => handleSourceModeChange('existing')}
            />
            <SourceModeButton
              active={sourceMode === 'empty'}
              icon={<AddIcon />}
              label="לוח ריק"
              onClick={() => handleSourceModeChange('empty')}
            />
          </Box>

          {/* ── Templates mode content ── */}
          {sourceMode === 'templates' && (
            <>
              {/* Search */}
              <Box sx={{ p: 1.5, borderBottom: `1px solid ${border}`, flexShrink: 0 }}>
                <TextField
                  size="small" fullWidth
                  placeholder="חיפוש תבנית..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: slate, fontSize: '1.1rem' }} />
                      </InputAdornment>
                    ),
                    sx: { fontSize: '0.85rem', bgcolor: 'white', borderRadius: '8px' },
                  }}
                />
              </Box>

              {/* Category chips */}
              <Box sx={{ px: 1.5, py: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, borderBottom: `1px solid ${border}`, flexShrink: 0 }}>
                <Chip
                  key="all" label="הכל" size="small"
                  onClick={() => setActiveCategory('all')}
                  sx={{
                    bgcolor: activeCategory === 'all' ? navy : 'white',
                    color:   activeCategory === 'all' ? 'white' : slate,
                    border:  `1px solid ${activeCategory === 'all' ? navy : border}`,
                    fontWeight: 600, fontSize: '0.75rem',
                    '&:hover': { bgcolor: activeCategory === 'all' ? navy : '#f0f4f8' },
                  }}
                />
                {categories.map(cat => (
                  <Chip
                    key={cat} label={CATEGORY_LABELS[cat] || cat} size="small"
                    onClick={() => setActiveCategory(cat)}
                    sx={{
                      bgcolor: activeCategory === cat ? navy : 'white',
                      color:   activeCategory === cat ? 'white' : slate,
                      border:  `1px solid ${activeCategory === cat ? navy : border}`,
                      fontWeight: 600, fontSize: '0.75rem',
                      '&:hover': { bgcolor: activeCategory === cat ? navy : '#f0f4f8' },
                    }}
                  />
                ))}
              </Box>

              {/* Template list */}
              <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5, '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#dde1e7', borderRadius: 2 } }}>
                {filteredTemplates.map(template => {
                  const isSelected = template.id === selectedTemplateId;
                  return (
                    <Box
                      key={template.id}
                      onClick={() => onSelectTemplate(template.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        px: 1.5, py: 1,
                        cursor: 'pointer',
                        borderRight: isSelected ? `3px solid ${navy}` : '3px solid transparent',
                        bgcolor: isSelected ? '#f0f4f8' : 'transparent',
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: isSelected ? '#e8edf4' : '#f5f7f9' },
                      }}
                    >
                      <MiniThumbnail template={template} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: isSelected ? 700 : 500, color: isSelected ? navy : '#1a202c', lineHeight: 1.3 }} noWrap>
                          {template.name || 'תבנית'}
                        </Typography>
                        {template.category && (
                          <Typography sx={{ fontSize: '0.72rem', color: slate, mt: 0.2 }}>
                            {CATEGORY_LABELS[template.category] || template.category}
                          </Typography>
                        )}
                      </Box>
                      {isSelected && <CheckIcon sx={{ color: navy, fontSize: '1rem', flexShrink: 0 }} />}
                    </Box>
                  );
                })}
                {filteredTemplates.length === 0 && (
                  <Typography sx={{ textAlign: 'center', color: slate, fontSize: '0.82rem', py: 4 }}>
                    לא נמצאו תבניות
                  </Typography>
                )}
              </Box>
            </>
          )}

          {/* ── Existing boards mode content ── */}
          {sourceMode === 'existing' && (
            <>
              {/* Search */}
              <Box sx={{ p: 1.5, borderBottom: `1px solid ${border}`, flexShrink: 0 }}>
                <TextField
                  size="small" fullWidth
                  placeholder="חיפוש לוח..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: slate, fontSize: '1.1rem' }} />
                      </InputAdornment>
                    ),
                    sx: { fontSize: '0.85rem', bgcolor: 'white', borderRadius: '8px' },
                  }}
                />
              </Box>

              {/* Existing dashboards list */}
              <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5, '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#dde1e7', borderRadius: 2 } }}>
                {filteredExisting.map(item => {
                  const isSelected = item.id === selectedTemplateId;
                  return (
                    <Box
                      key={item.id}
                      onClick={() => onSelectTemplate(item.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        px: 1.5, py: 1,
                        cursor: 'pointer',
                        borderRight: isSelected ? `3px solid ${navy}` : '3px solid transparent',
                        bgcolor: isSelected ? '#f0f4f8' : 'transparent',
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: isSelected ? '#e8edf4' : '#f5f7f9' },
                      }}
                    >
                      <MiniThumbnail template={item} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: isSelected ? 700 : 500, color: isSelected ? navy : '#1a202c', lineHeight: 1.3 }} noWrap>
                          {item.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: slate, mt: 0.2 }}>
                          {item.widgetCount} רכיבים
                        </Typography>
                      </Box>
                      {isSelected && <CheckIcon sx={{ color: navy, fontSize: '1rem', flexShrink: 0 }} />}
                    </Box>
                  );
                })}
                {filteredExisting.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography sx={{ color: slate, fontSize: '0.82rem' }}>
                      {existingItems.length === 0 ? 'אין לוחות קיימים' : 'לא נמצאו לוחות'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* ── Empty mode content ── */}
          {sourceMode === 'empty' && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
              <Box sx={{ textAlign: 'center', color: slate }}>
                <Typography sx={{ fontSize: '2.5rem', lineHeight: 1, mb: 1.5 }}>🖼️</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: navy, mb: 1 }}>
                  לוח ריק
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.6 }}>
                  הלוח ייווצר ריק ותוכל להוסיף
                  <br />
                  רכיבים לפי הטעם והצורך שלך
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* ── CENTER PANEL: preview ──────────────────────────────────── */}
        <BoardPreview
          template={selected}
          organization={organization}
          catalog={catalog}
          dispatch={dispatch}
        />

        {/* ── RIGHT PANEL: info ──────────────────────────────────────── */}
        <Box sx={{
          width: 270, flexShrink: 0,
          borderRight: `1px solid ${border}`,
          overflowY: 'auto', p: 2.5,
          bgcolor: 'white',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#dde1e7', borderRadius: 2 },
        }}>
          {selected ? (
            <>
              {/* Name + category badge */}
              <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: navy, mb: 0.75, lineHeight: 1.3 }}>
                {selected.name || (selected.id === 'empty' ? 'לוח ריק' : 'תבנית')}
              </Typography>
              {selected.category && selected.id !== 'empty' && !selected.id?.startsWith('copy:') && (
                <Chip
                  label={CATEGORY_LABELS[selected.category] || selected.category}
                  size="small"
                  sx={{ bgcolor: '#e8f0fb', color: navy, fontWeight: 700, fontSize: '0.72rem', mb: 1.5, height: 22 }}
                />
              )}

              {/* Copy badge for existing boards */}
              {selected.id?.startsWith('copy:') && (
                <Chip
                  label="העתקה מלוח קיים"
                  size="small"
                  icon={<ContentCopyIcon sx={{ fontSize: '0.75rem !important' }} />}
                  sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700, fontSize: '0.72rem', mb: 1.5, height: 22 }}
                />
              )}

              {/* Concept description — templates only */}
              {selected.concept && (
                <Typography sx={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.65, mb: 2 }}>
                  {selected.concept}
                </Typography>
              )}

              {/* When to use — templates only */}
              {selected.whenToUse && selected.id !== 'empty' && (
                <Box sx={{ bgcolor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', p: 1.5, mb: 2 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#0c4a6e', mb: 0.4 }}>✅ מתי להשתמש</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#0c4a6e', lineHeight: 1.55 }}>
                    {selected.whenToUse}
                  </Typography>
                </Box>
              )}

              {/* Included widgets — templates with includedWidgets */}
              {selected.includedWidgets?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: slate, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>
                    רכיבים בתבנית
                  </Typography>
                  {selected.includedWidgets.map((wgt, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 0.75, mb: 0.6 }}>
                      <Box sx={{ width: 5, height: 5, bgcolor: navy, borderRadius: '50%', mt: '6px', flexShrink: 0 }} />
                      <Box>
                        <Typography component="span" sx={{ fontSize: '0.78rem', fontWeight: 600, color: navy }}>
                          {wgt.name}
                        </Typography>
                        {wgt.reason && (
                          <Typography component="span" sx={{ fontSize: '0.75rem', color: slate }}> — {wgt.reason}</Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Widget list — existing boards (derive from widgets array) */}
              {selected.id?.startsWith('copy:') && selected.widgets?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: slate, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>
                    רכיבים בלוח ({selected.widgets.length})
                  </Typography>
                  {selected.widgets.map((widget, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 0.75, mb: 0.6 }}>
                      <Box sx={{ width: 5, height: 5, bgcolor: navy, borderRadius: '50%', mt: '6px', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: navy }}>
                        {getWidgetName(widget.componentKey)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Setup reminder — templates only */}
              {selected.id !== 'empty' && !selected.id?.startsWith('copy:') && (
                <Box sx={{
                  bgcolor: '#fefce8', border: '1.5px solid #fde047',
                  borderRadius: '8px', p: 1.5, mb: 2,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <TuneIcon sx={{ fontSize: '0.85rem', color: '#a16207' }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#713f12' }}>
                      מומלץ להגדיר כל רכיב
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.75rem', color: '#854d0e', lineHeight: 1.55 }}>
                    הרכיבים מגיעים עם <strong>הגדרות ברירת מחדל ונתוני דמה</strong>. לאחר הוספת הלוח, לחץ על כל רכיב ועדכן את ההגדרות שלו.
                  </Typography>
                </Box>
              )}

              {/* Copy info — existing boards */}
              {selected.id?.startsWith('copy:') && (
                <Box sx={{
                  bgcolor: '#f0f9ff', border: '1.5px solid #7dd3fc',
                  borderRadius: '8px', p: 1.5, mb: 2,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <ContentCopyIcon sx={{ fontSize: '0.85rem', color: '#0369a1' }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#0c4a6e' }}>
                      העתקה מלוח קיים
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.75rem', color: '#0c4a6e', lineHeight: 1.55 }}>
                    ייווצר לוח חדש עם כל הרכיבים וההגדרות מהלוח שנבחר. הלוח המקורי לא יושפע.
                  </Typography>
                </Box>
              )}

              {/* Suggested with — templates only */}
              {selected.suggestedWith?.length > 0 && (() => {
                const lookup = Object.fromEntries((actualTemplates || []).map(t => [t.id, t.name]));
                const names = selected.suggestedWith.map(id => lookup[id]).filter(Boolean);
                if (!names.length) return null;
                return (
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: slate, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>
                      מתאים לרוטציה עם
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {names.map((name, i) => (
                        <Chip key={i} label={name} size="small"
                          sx={{ bgcolor: '#f0f4f8', color: navy, fontSize: '0.7rem', fontWeight: 600, height: 20, border: `1px solid ${border}` }}
                        />
                      ))}
                    </Box>
                  </Box>
                );
              })()}
            </>
          ) : (
            <Typography sx={{ fontSize: '0.85rem', color: slate, textAlign: 'center', mt: 4 }}>
              {sourceMode === 'templates' && 'בחר תבנית מהרשימה כדי לראות פרטים'}
              {sourceMode === 'existing' && 'בחר לוח מהרשימה כדי לראות תצוגה מקדימה'}
              {sourceMode === 'empty' && ''}
            </Typography>
          )}
        </Box>

      </Box>
    </Box>
  );
};

export default DashboardTemplateSelector;
